import { Switch } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp, faCopy, faPen } from '@fortawesome/free-solid-svg-icons'
import {
  describeAmount,
  describeCaps,
  describeConditions,
  describeRoute,
  expirationState
} from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'

const Chip = ({ children, tone = 'neutral' }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
      tone === 'warning'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
        : 'bg-gray-100 text-gray-700 dark:bg-dk-raised dark:text-gray-300'
    }`}
  >
    {children}
  </span>
)

const ArrowButton = ({ icon, label, onClick, disabled, title }) => (
  <button
    className={`w-7 h-6 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover disabled:opacity-30 disabled:cursor-not-allowed ${MUTED}`}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={title}
  >
    <FontAwesomeIcon icon={icon} className='text-xs' />
  </button>
)

export default function DiscountRow({
  discount,
  position,
  isFirst,
  isLast,
  canReorder,
  busy,
  onMove,
  onToggle,
  onEdit,
  onDuplicate
}) {
  const caps = describeCaps(discount)
  const expiration = expirationState(discount.expires_at)
  const conditions = describeConditions(discount)
  const reorderHint = canReorder ? undefined : 'Borrá la búsqueda para cambiar el orden'

  return (
    <li className='flex items-start gap-3 px-4 py-4'>
      <div className='flex flex-col items-center gap-0.5 pt-0.5'>
        <ArrowButton
          icon={faChevronUp}
          label={`Subir ${discount.code}`}
          onClick={() => onMove(-1)}
          disabled={!canReorder || busy || isFirst}
          title={reorderHint}
        />
        <span className={`text-xs tabular-nums ${MUTED}`} aria-label={`Prioridad ${position}`}>
          {position}
        </span>
        <ArrowButton
          icon={faChevronDown}
          label={`Bajar ${discount.code}`}
          onClick={() => onMove(1)}
          disabled={!canReorder || busy || isLast}
          title={reorderHint}
        />
      </div>

      <Switch
        checked={!!discount.active}
        onChange={onToggle}
        disabled={busy}
        inputProps={{ 'aria-label': `${discount.active ? 'Apagar' : 'Prender'} ${discount.code}` }}
        sx={{ flexShrink: 0, mt: -0.5 }}
      />

      <div className={`min-w-0 flex-1 ${discount.active ? '' : 'opacity-60'}`}>
        <p className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
          <span className='font-semibold text-gray-900 dark:text-gray-100'>{discount.code}</span>
          <span className='text-sm font-medium text-primary'>{describeAmount(discount)}</span>
          {!discount.active && <Chip>Apagado</Chip>}
        </p>
        <p className='text-sm text-gray-700 dark:text-gray-200 mt-0.5'>
          {describeRoute(discount)}
          <span className={MUTED}> · radio {discount.margin} km</span>
        </p>
        <p className={`text-xs mt-1 ${MUTED}`}>
          {caps.label}
          {caps.isDefault && ' (por defecto)'}
          {expiration && !expiration.expired && ` · Vence el ${expiration.label}`}
        </p>
        {(conditions.length > 0 || expiration?.expired) && (
          <div className='flex flex-wrap gap-1.5 mt-2'>
            {expiration?.expired && (
              <Chip tone='warning'>
                Venció el {expiration.label}
                {discount.active && ' y sigue activo'}
              </Chip>
            )}
            {conditions.map(condition => (
              <Chip key={condition}>{condition}</Chip>
            ))}
          </div>
        )}
      </div>

      <div className='shrink-0 flex items-center gap-1'>
        <button
          className={`w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover ${MUTED}`}
          onClick={onDuplicate}
          aria-label={`Duplicar ${discount.code}`}
          title='Duplicar'
        >
          <FontAwesomeIcon icon={faCopy} className='text-sm' />
        </button>
        <button
          className={`w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover ${MUTED}`}
          onClick={onEdit}
          aria-label={`Editar ${discount.code}`}
          title='Editar'
        >
          <FontAwesomeIcon icon={faPen} className='text-sm' />
        </button>
      </div>
    </li>
  )
}
